// AM-030: minimal, dependency-free editor for the routing operation
// AND/OR join graph. Deliberately list-based (not a full drag-and-drop
// canvas like the ADR-103 print designer) since the dependency graph for
// a single routing revision is small (tens of operations) and a clear,
// keyboard-accessible list is more reliable for this use case than a
// canvas widget.
import React, { useMemo, useState } from "react";
import { useAm030GraphStore } from "./am030-store";
import {
  useCreateRoutingOperationDependency,
  useDeleteRoutingOperationDependency,
  useRoutingOperationDependencies,
  useUpdateOperationJoinType,
} from "./am030-hooks";
import { OperationJoinType, RoutingOperationSummary } from "./am030-types";
import mermaid from "mermaid";

function Mermaid({ chart }: { chart: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    mermaid.initialize({ startOnLoad: false, securityLevel: "loose", theme: "base", themeVariables: { primaryColor: '#f4f4f4', edgeLabelBackground: '#ffffff', tertiaryColor: '#fff' } });
    let isMounted = true;

    const renderChart = async () => {
      try {
        if (!containerRef.current) return;
        const { svg, bindFunctions } = await mermaid.render(`mermaid-${Math.random().toString(36).substr(2, 9)}`, chart);
        
        if (!isMounted || !containerRef.current) return;
        containerRef.current.innerHTML = svg;
        
        if (bindFunctions) {
          bindFunctions(containerRef.current);
        }
      } catch (e) {
        console.error("Mermaid error:", e);
      }
    };
    renderChart();

    return () => {
      isMounted = false;
    };
  }, [chart]);

  return <div ref={containerRef} style={{ direction: "ltr", textAlign: "center", overflowX: "auto" }} />;
}

interface DependencyGraphEditorProps {
  routingRevisionId: string;
  operations: RoutingOperationSummary[];
  /** Read-only mode is required once the routing revision is Published (Rule JOIN-06). */
  readOnly?: boolean;
  onNodeClick?: (operationId: string) => void;
}

export function DependencyGraphEditor({
  routingRevisionId,
  operations,
  readOnly = false,
  onNodeClick,
}: DependencyGraphEditorProps) {
  const { data } = useRoutingOperationDependencies(routingRevisionId);
  const edges: any[] = (data as any)?.results ?? (data as any) ?? [];
  const createEdge = useCreateRoutingOperationDependency();
  const deleteEdge = useDeleteRoutingOperationDependency(routingRevisionId);
  const updateJoinType = useUpdateOperationJoinType(routingRevisionId);

  const validateCandidateEdge = useAm030GraphStore((s) => s.validateCandidateEdge);
  const setEdges = useAm030GraphStore((s) => s.setEdges);
  const setOperations = useAm030GraphStore((s) => s.setOperations);

  React.useEffect(() => setEdges(edges), [edges, setEdges]);
  React.useEffect(() => setOperations(operations), [operations, setOperations]);

  const [upstreamId, setUpstreamId] = useState<string>("");
  const [downstreamId, setDownstreamId] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  const operationsById = useMemo(() => {
    const map = new Map<string, RoutingOperationSummary>();
    operations.forEach((op) => map.set(op.id, op));
    return map;
  }, [operations]);

  const edgesByDownstream = useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((e: any) => {
      const down = e.downstream_operation ?? e.downstreamOperation;
      const up = e.upstream_operation ?? e.upstreamOperation;
      const list = map.get(down) ?? [];
      list.push(up);
      map.set(down, list);
    });
    return map;
  }, [edges]);

  function handleAddEdge() {
    setFormError(null);
    if (upstreamId === "" || downstreamId === "") {
      setFormError("عملیات مبدأ و مقصد را انتخاب کنید.");
      return;
    }
    const error = validateCandidateEdge({
      routingRevision: routingRevisionId,
      upstreamOperation: upstreamId,
      downstreamOperation: downstreamId,
    });
    if (error) {
      setFormError(error);
      return;
    }
    createEdge.mutateAsync({
      routingRevision: String(routingRevisionId),
      upstreamOperation: String(upstreamId),
      downstreamOperation: String(downstreamId),
    }).then(() => {
      setUpstreamId("");
      setDownstreamId("");
      setFormError(null);
    }).catch((err: any) => {
      const apiErr = err.response?.data;
      setFormError(apiErr ? JSON.stringify(apiErr) : (err instanceof Error ? err.message : "خطا در ثبت وابستگی."));
    });
  }

  // Generate Mermaid graph based on operations and edges
  const graphDefinition = useMemo(() => {
    let def = "graph TD;\n";
    if (operations.length === 0) return "graph TD;\n  Empty[بدون عملیات]";
    
    // Add nodes
    operations.forEach(op => {
      // Sanitize label: remove double quotes or replace with single quotes to prevent breaking the Mermaid parser
      const label = (op.operationCode || "").replace(/"/g, "'");
      def += `  op_${op.id}("${label}")\n`;
      if (onNodeClick) {
        def += `  click op_${op.id} call handleMermaidNodeClick()\n`;
      }
    });
    
    // Add edges
    edges.forEach(e => {
      const up = e.upstream_operation ?? e.upstreamOperation;
      const down = e.downstream_operation ?? e.downstreamOperation;
      if (operationsById.has(up) && operationsById.has(down)) {
        const downOp = operationsById.get(down);
        const joinLabel = downOp?.upstreamJoinType === "OR" ? "|OR|" : "";
        def += `  op_${up} -->${joinLabel} op_${down}\n`;
      }
    });
    return def;
  }, [operations, edges, operationsById, onNodeClick]);

  return (
    <div data-testid="am030-dependency-editor" style={{ direction: "rtl", marginTop: "2rem" }}>
      <h3 style={{ marginBottom: "1rem", color: "#333" }}>وابستگی‌های عملیات (AND / OR)</h3>

      <div style={{ marginBottom: "1.5rem", background: "#FFFFFF", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)", padding: "1rem" }}>
        <h4 style={{ marginBottom: "1rem", color: "#666" }}>نمای گرافیکی</h4>
        <Mermaid chart={graphDefinition} />
      </div>

      <div style={{ overflowX: "auto", background: "#FFFFFF", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)", padding: "1rem" }}>
        <table data-testid="am030-operations-table" style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#007270" }}>
              <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>کد عملیات</th>
              <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>ترتیب پیش‌نیازها</th>
              <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>پیش‌نیازها</th>
            </tr>
          </thead>
        <tbody>
          {operations.map((op) => {
            const upstreamIds = edgesByDownstream.get(op.id) ?? [];
            return (
              <tr key={op.id} data-testid={`am030-op-row-${op.id}`} style={{ borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                <td style={{ padding: "0.75rem 1rem", fontWeight: "800", color: "#D4AF37" }}>{op.operationCode}</td>
                <td style={{ padding: "0.75rem 1rem" }}>
                  <select
                    aria-label={`join-type-${op.id}`}
                    value={op.upstreamJoinType}
                    disabled={readOnly || upstreamIds.length < 2}
                    style={{ padding: "6px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", outline: "none", fontSize: "0.85rem", background: "var(--color-surface, #F5F1E6)", color: "#2B2B2B" }}
                    onChange={(e) =>
                      updateJoinType.mutate({
                        id: op.id.toString(),
                        data: {
                          upstream_join_type: e.target.value as OperationJoinType,
                        },
                      })
                    }
                  >
                    <option value="AND">AND (همه لازم)</option>
                    <option value="OR">OR (هرکدام کافی)</option>
                  </select>
                </td>
                <td style={{ padding: "0.75rem 1rem" }}>
                  {upstreamIds.length === 0 && <span style={{ color: "#999" }}>—</span>}
                  {upstreamIds.map((upId) => {
                    const edge = edges.find(
                      (e: any) =>
                        (e.upstream_operation ?? e.upstreamOperation) === upId &&
                        (e.downstream_operation ?? e.downstreamOperation) === op.id
                    );
                    return (
                      <span key={upId} style={{ marginInlineEnd: 8, padding: "4px 8px", background: "rgba(0,114,112,0.08)", color: "#007270", borderRadius: "6px", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        {operationsById.get(upId)?.operationCode ?? upId}
                        {!readOnly && edge && (
                          <button
                            aria-label={`remove-edge-${edge.id}`}
                            style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", padding: "0 4px", fontSize: "1rem", fontWeight: "bold" }}
                            onClick={() => deleteEdge.mutate({ id: edge.id })}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {!readOnly && (
        <div data-testid="am030-add-edge-form" style={{ marginTop: 16, display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", padding: "1rem", background: "#FFFFFF", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)" }}>
          <select
            aria-label="upstream-operation-select"
            style={{ padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", outline: "none", background: "var(--color-surface, #F5F1E6)" }}
            value={upstreamId}
            onChange={(e) => setUpstreamId(e.target.value)}
          >
            <option value="">عملیات مبدأ (پیش‌نیاز)</option>
            {operations.map((op) => (
              <option key={op.id} value={op.id}>
                {op.operationCode}
              </option>
            ))}
          </select>

          <span style={{ color: "#666" }}>➜</span>

          <select
            aria-label="downstream-operation-select"
            style={{ padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", outline: "none", background: "var(--color-surface, #F5F1E6)" }}
            value={downstreamId}
            onChange={(e) => setDownstreamId(e.target.value)}
          >
            <option value="">عملیات مقصد</option>
            {operations.map((op) => (
              <option key={op.id} value={op.id}>
                {op.operationCode}
              </option>
            ))}
          </select>

          <button 
            style={{ padding: "8px 16px", borderRadius: "8px", background: "#007270", color: "#FFFFFF", fontWeight: "700", border: "none", cursor: "pointer", marginRight: "auto" }}
            onClick={handleAddEdge} 
            disabled={createEdge.isPending}
          >
            افزودن وابستگی
          </button>

          {formError && (
            <p role="alert" data-testid="am030-form-error" style={{ color: "#EF4444", width: "100%", margin: "8px 0 0 0", fontSize: "0.9rem" }}>
              {formError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
